import { useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { suppliersApi, type Supplier, type SupplierReview } from "@/lib/api";

type SupplierWithReview = Supplier & { latest_review?: SupplierReview };

type Recommendation = "Auto-Renew" | "Conditional Renewal" | "Re-Advertise";

const recColors: Record<Recommendation, { bg: string; text: string; dot: string }> = {
  "Auto-Renew": { bg: "bg-secondary-50", text: "text-secondary-700", dot: "bg-secondary-500" },
  "Conditional Renewal": { bg: "bg-accent-50", text: "text-accent-700", dot: "bg-accent-500" },
  "Re-Advertise": { bg: "bg-foreground-100", text: "text-foreground-600", dot: "bg-foreground-400" },
};

function getScoreColor(score: number): string {
  if (score >= 4.0) return "text-secondary-600 bg-secondary-50";
  if (score >= 2.5) return "text-accent-600 bg-accent-50";
  return "text-foreground-500 bg-foreground-100";
}

function renderStars(score: number) {
  const full = Math.floor(score);
  const half = score - full >= 0.5;
  return Array.from({ length: 5 }, (_, i) => (
    <i key={i} className={`text-xs ${i < full ? "ri-star-fill text-accent-500" : i === full && half ? "ri-star-half-line text-accent-500" : "ri-star-line text-foreground-300"}`}></i>
  ));
}

export default function SupplierReviewsList() {
  const navigate = useNavigate();
  const [suppliers, setSuppliers] = useState<SupplierWithReview[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [recFilter, setRecFilter] = useState<Recommendation | "All">("All");

  useEffect(() => {
    setLoading(true);
    suppliersApi.list(1, { withLatestReview: true })
      .then((res) => setSuppliers(res.data.data as SupplierWithReview[]))
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  const filtered = suppliers.filter((s) => {
    if (search && !s.name.toLowerCase().includes(search.toLowerCase())) return false;
    if (recFilter !== "All" && s.latest_review?.recommendation !== recFilter) return false;
    return true;
  });

  const counts = {
    All: suppliers.length,
    "Auto-Renew": suppliers.filter((s) => s.latest_review?.recommendation === "Auto-Renew").length,
    "Conditional Renewal": suppliers.filter((s) => s.latest_review?.recommendation === "Conditional Renewal").length,
    "Re-Advertise": suppliers.filter((s) => s.latest_review?.recommendation === "Re-Advertise").length,
  };

  const avgRating = suppliers.length > 0
    ? (suppliers.reduce((acc, s) => acc + parseFloat(s.average_rating || "0"), 0) / suppliers.length).toFixed(1)
    : "—";

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
        <div>
          <h1 className="text-primary-600 text-xl md:text-2xl font-bold">Supplier Reviews</h1>
          <p className="text-foreground-500 text-sm mt-1">Quarterly performance ratings and renewal recommendations</p>
        </div>
        <button onClick={() => navigate("/supplier-reviews/new")}
          className="h-9 px-4 bg-primary-600 text-white text-sm font-medium rounded-md hover:bg-primary-700 transition-colors whitespace-nowrap flex items-center gap-1.5 cursor-pointer">
          <i className="ri-add-line text-sm"></i><span>New Review</span>
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        <div className="bg-white rounded-lg border border-background-200 p-4">
          <p className="text-foreground-400 text-xs font-medium uppercase tracking-wider mb-2">Avg Rating</p>
          <p className="text-foreground-900 text-2xl font-bold">{avgRating}</p>
          <p className="text-foreground-500 text-xs mt-0.5">across all suppliers</p>
        </div>
        <div className="bg-white rounded-lg border border-background-200 p-4">
          <p className="text-foreground-400 text-xs font-medium uppercase tracking-wider mb-2">Auto-Renew</p>
          <p className="text-secondary-600 text-2xl font-bold">{counts["Auto-Renew"]}</p>
          <p className="text-foreground-500 text-xs mt-0.5">recommended</p>
        </div>
        <div className="bg-white rounded-lg border border-background-200 p-4">
          <p className="text-foreground-400 text-xs font-medium uppercase tracking-wider mb-2">Conditional</p>
          <p className="text-accent-600 text-2xl font-bold">{counts["Conditional Renewal"]}</p>
          <p className="text-foreground-500 text-xs mt-0.5">needs improvement</p>
        </div>
        <div className="bg-white rounded-lg border border-background-200 p-4">
          <p className="text-foreground-400 text-xs font-medium uppercase tracking-wider mb-2">Re-Advertise</p>
          <p className="text-foreground-600 text-2xl font-bold">{counts["Re-Advertise"]}</p>
          <p className="text-foreground-500 text-xs mt-0.5">not recommended</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg border border-background-200 rounded-b-none border-b-0 p-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
            <input type="text" placeholder="Search suppliers..." value={search} onChange={(e) => setSearch(e.target.value)}
              className="w-full h-9 pl-9 pr-3 text-sm border border-background-300 rounded-md bg-white text-foreground-700 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors" />
          </div>
          <div className="flex gap-1.5 flex-wrap">
            {(["All", "Auto-Renew", "Conditional Renewal", "Re-Advertise"] as const).map((opt) => (
              <button key={opt} onClick={() => setRecFilter(opt)}
                className={`h-9 px-3 text-sm font-medium rounded-md whitespace-nowrap transition-colors cursor-pointer ${recFilter === opt ? "bg-primary-600 text-white" : "bg-background-100 text-foreground-600 hover:bg-background-200"}`}>
                {opt}
                <span className={`ml-1.5 text-xs ${recFilter === opt ? "text-white/70" : "text-foreground-400"}`}>{counts[opt]}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg border border-background-200 rounded-t-none overflow-hidden">
        {loading ? (
          <div className="py-12 text-center text-foreground-400 text-sm">
            <i className="ri-loader-4-line animate-spin mr-2"></i>Loading suppliers...
          </div>
        ) : error ? (
          <div className="py-12 text-center text-accent-600 text-sm">{error}</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-background-200 bg-background-100">
                  <th className="text-left text-xs font-semibold text-foreground-500 uppercase tracking-wider px-4 py-3">Supplier</th>
                  <th className="text-center text-xs font-semibold text-foreground-500 uppercase tracking-wider px-4 py-3">Latest Quarter</th>
                  <th className="text-center text-xs font-semibold text-foreground-500 uppercase tracking-wider px-4 py-3">Avg Rating</th>
                  <th className="text-center text-xs font-semibold text-foreground-500 uppercase tracking-wider px-4 py-3">Reviews</th>
                  <th className="text-center text-xs font-semibold text-foreground-500 uppercase tracking-wider px-4 py-3">Recommendation</th>
                  <th className="text-center text-xs font-semibold text-foreground-500 uppercase tracking-wider px-4 py-3">Status</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((s) => {
                  const rec = s.latest_review?.recommendation as Recommendation | undefined;
                  const rc = rec ? recColors[rec] : null;
                  const avgScore = parseFloat(s.average_rating || "0");
                  const quarter = s.latest_review?.quarter;
                  const year = s.latest_review?.year;
                  return (
                    <tr key={s.id} onClick={() => navigate(`/supplier-reviews/${s.id}`)}
                      className="border-b border-background-100 hover:bg-background-100 transition-colors cursor-pointer">
                      <td className="px-4 py-3">
                        <p className="text-foreground-800 text-sm font-medium">{s.name}</p>
                        {s.contact_person && <p className="text-foreground-400 text-xs">{s.contact_person}</p>}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className="text-foreground-600 text-sm">
                          {quarter && year ? `Q${quarter} ${year}` : "—"}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        {avgScore > 0 ? (
                          <div className="inline-flex items-center gap-1.5">
                            <span className={`inline-flex items-center justify-center w-9 h-9 rounded-full text-sm font-bold ${getScoreColor(avgScore)}`}>
                              {avgScore.toFixed(1)}
                            </span>
                            <div className="flex">{renderStars(avgScore)}</div>
                          </div>
                        ) : <span className="text-foreground-400 text-sm">—</span>}
                      </td>
                      <td className="px-4 py-3 text-center text-foreground-600 text-sm">{s.total_reviews}</td>
                      <td className="px-4 py-3 text-center">
                        {rc ? (
                          <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium ${rc.bg} ${rc.text}`}>
                            <span className={`w-1.5 h-1.5 rounded-full ${rc.dot}`}></span>{rec}
                          </span>
                        ) : <span className="text-foreground-400 text-xs">No review yet</span>}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium capitalize ${s.status === "active" ? "bg-secondary-50 text-secondary-700" : "bg-foreground-100 text-foreground-600"}`}>
                          {s.status}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {filtered.length === 0 && (
              <div className="py-12 text-center text-foreground-400 text-sm">
                {suppliers.length === 0 ? "No suppliers yet. Add a supplier to get started." : "No suppliers match your filters."}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
